/* eslint-disable react/no-unstable-nested-components */
import React, { useState } from "react";
import { NetworkStatus, useQuery } from "@apollo/client";
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { LockIcon, PencilIcon, PinIcon } from "lucide-react";
import type { CellContext } from "@tanstack/react-table";
import parseISO from "date-fns/parseISO";
import Link from "next/link";
import { FormattedMessage } from "react-intl";

import { ForumTopicCardSekeleton } from "components/forum/ForumTopicCard";
import CreatePostForm from "components/forum/CreatePostForm";
import SadFaceIcon from "components/icons/SadFaceIcon";
import classNames from "components/ui/classNames";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "components/ui/Dialog";
import Avatar from "components/core/Avatar";
import RelativeTime from "components/ui/RelativeTime";

import PostsQuery from "lib/graphql/queries/engagement/PostsQuery";
import type {
  IPostsQuery,
  IPostsQueryVariables,
} from "lib/graphql/queries/engagement/__generated__/PostsQuery.generated";
import useSelfie from "lib/hooks/useSelfie";
import type { IPostContextType } from "types/graphqlTypes";
import { ScrollArea } from "components/ui/ScrollArea";
import ContentContainerBox from "components/stocks/ContentContainerBox";

type ForumPost = IPostsQuery["posts"]["nodes"][0];

interface ForumProps {
  contextKey: string | null;
  contextType: IPostContextType;
}

export function Forum({ contextKey, contextType }: ForumProps) {
  const selfie = useSelfie();
  const [isNewTopicOpen, setIsNewTopicOpen] = useState(false);
  const columnHelper = createColumnHelper<ForumPost>();

  const { data, loading, fetchMore, networkStatus } = useQuery<IPostsQuery, IPostsQueryVariables>(PostsQuery, {
    variables: {
      contextKey,
      contextType,
      limit: 30,
      cursor: null,
      filterBy: null,
      orderBy: null,
    },
    notifyOnNetworkStatusChange: true,
  });

  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;

  const columns = [
    columnHelper.accessor("title", {
      header: () => (
        <div
          className={classNames(
            "bg-slate-50 px-2 py-1.5 text-sm font-semibold text-slate-700",
            "dark:bg-slate-800 dark:text-slate-300",
          )}
        >
          <FormattedMessage defaultMessage="Tópico" id="27nQDY" />
        </div>
      ),
      cell: TopicItem,
    }),
    columnHelper.display({
      id: "Interagido Por",
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Interações" id="H2EfTK" />
        </div>
      ),
      cell: AvatarListForViewedBy,
    }),
    columnHelper.display({
      id: "Replies",
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Respostas" id="g24R0e" />
        </div>
      ),
      cell: (props) => (
        <div
          className={classNames("whitespace-nowrap", "p-5 text-sm font-normal", "text-slate-600 dark:text-slate-400")}
        >
          <span>{props.row.original.replies.totalCount}</span>
        </div>
      ),
    }),
    columnHelper.accessor("lastRepliedAt", {
      header: () => (
        <div className="overflow-hidden text-ellipsis px-4 py-1.5 text-sm font-semibold text-slate-600">
          <FormattedMessage defaultMessage="Atividade" id="KVhP+Y" />
        </div>
      ),
      cell: Activity,
    }),
  ];

  const table = useReactTable({
    data: data?.posts.nodes ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <ContentContainerBox
      className={classNames(
        "mx-auto grid w-full max-w-7xl grid-cols-4 gap-x-4 gap-y-6 px-4 pb-6",
        "md:grid-cols-12 md:gap-9 md:gap-y-8",
      )}
    >
      <div className="col-span-4 w-full md:col-span-3 lg:col-span-2 lg:col-start-11">
        {selfie && selfie.isPremium && (
          <Dialog open={isNewTopicOpen} onOpenChange={() => setIsNewTopicOpen((prev) => !prev)}>
            <DialogTrigger className="flex h-10 w-full items-center justify-center space-x-2 rounded-md bg-brand text-white">
              <PencilIcon className="h-5 w-5" />
              <span className="font-aeonik font-bold">
                <FormattedMessage description="Listagem de tópicos do fórum" defaultMessage="Novo Tópico" id="qWXPan" />
              </span>
            </DialogTrigger>
            <DialogContent className="overflo max-h-[90%] py-5 sm:max-w-[425px] lg:max-w-3xl">
              <DialogHeader>
                <DialogTitle>
                  <FormattedMessage
                    description="Listagem de tópicos do fórum"
                    defaultMessage="Novo Tópico"
                    id="qWXPan"
                  />
                </DialogTitle>
                <DialogDescription>
                  <FormattedMessage
                    description="Listagem de tópicos do fórum"
                    defaultMessage="Crie um novo tópico no Fórum. Preencha as informações e clique em salvar"
                    id="bNk4fY"
                  />
                </DialogDescription>
              </DialogHeader>

              <CreatePostForm
                contextKey={contextKey}
                contextType={contextType}
                onClose={() => setIsNewTopicOpen((prev) => !prev)}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="col-span-4 w-full md:col-span-12">
        {data && data.posts.nodes.length > 0 && (
          <ScrollArea type="always" orientation="horizontal" className="relative w-full">
            <div className="pb-4 lg:w-full">
              <div
                className={classNames(
                  "relative overflow-auto rounded-xl border border-slate-200",
                  "dark:border-slate-950",
                )}
              >
                <table className="w-full divide-y divide-slate-200 dark:divide-slate-700">
                  <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr
                        key={headerGroup.id}
                        className="select-none divide-x divide-slate-200 dark:divide-slate-700 [&>th]:whitespace-nowrap"
                      >
                        {headerGroup.headers
                          .filter((header) => !header.column.columnDef.meta?.isHidden)
                          .map((header) => {
                            return (
                              <th
                                key={header.id}
                                className={classNames(
                                  "m-0 p-0 text-center",
                                  header.column.columnDef.meta?.header?.className,
                                )}
                              >
                                {header.isPlaceholder
                                  ? null
                                  : flexRender(header.column.columnDef.header, header.getContext())}
                              </th>
                            );
                          })}
                      </tr>
                    ))}
                  </thead>
                  <tbody className="divide-y divide-slate-200 font-mono dark:divide-slate-700">
                    {table.getRowModel().rows.map((row) => {
                      return (
                        <tr key={row.id} className="group divide-x divide-gray-200 dark:divide-slate-700">
                          {row
                            .getVisibleCells()
                            .filter((cell) => !cell.column.columnDef.meta?.isHidden)
                            .map((cell) => {
                              return (
                                <td
                                  key={cell.id}
                                  className={classNames(
                                    "m-0 p-0 text-center",
                                    cell.column.columnDef.meta?.cell?.className,
                                  )}
                                >
                                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                              );
                            })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </ScrollArea>
        )}

        {!data ||
          (data && data.posts.nodes.length === 0 && (
            <div className="justify-center space-y-2 rounded-md p-8 text-center text-base tracking-wide text-gray-600">
              <div>
                <SadFaceIcon />
              </div>

              <p>
                <FormattedMessage
                  description="Listagem de tópicos do fórum"
                  defaultMessage="Nenhum tópico foi encontrado. Que tal você ser o primeiro a nos deixar sua pergunta?"
                  id="+uY4D8"
                />
              </p>
            </div>
          ))}
      </div>

      {data && data.posts.pageInfo.hasNextPage && (
        <div className="col-span-4 w-full justify-center md:col-span-12">
          <div className="pt-8 text-center">
            <button
              disabled={loading || isLoadingMore}
              type="button"
              className={classNames(
                "font-aeonik text-base font-medium tracking-wide text-blue-600 hover:underline",
                "disabled:text-gray-600",
              )}
              onClick={() => {
                fetchMore({
                  variables: {
                    cursor: data.posts.pageInfo.cursor,
                  },
                });
              }}
            >
              <FormattedMessage
                description="Listagem de tópicos do fórum"
                defaultMessage="Carregar mais tópicos"
                id="r9D9mW"
              />
            </button>
          </div>
        </div>
      )}
    </ContentContainerBox>
  );
}

interface IForumContainerProps {
  children: React.ReactNode;
}

export function ForumContainer({ children }: IForumContainerProps) {
  return (
    <div
      className={classNames(
        "mx-auto grid w-full max-w-7xl grid-cols-4 gap-x-4 gap-y-6 px-4 pb-12",
        "xl:grid-cols-12 xl:gap-9 xl:px-0",
      )}
    >
      {children}
    </div>
  );
}

export function ForumSkeleton() {
  return (
    <ForumContainer>
      <div className="col-span-4 animate-pulse space-y-0.5 font-aeonik md:col-span-12">
        <div className="h-8 w-52 bg-gray-300" />
        <div className="h-5 w-12 bg-gray-300" />
      </div>

      <ForumTopicCardSekeleton className="h-96" />

      {Array.from({ length: 2 }).map((_, index) => (
        <ForumTopicCardSekeleton key={`skeletonTopic/${String(index)}`} />
      ))}
    </ForumContainer>
  );
}

function AvatarListForViewedBy({ row }: CellContext<ForumPost, unknown>) {
  const participants = row.original.participants.nodes.map((participant) => participant);

  return (
    <span className="flex w-max flex-wrap gap-1 p-5">
      {participants.map((participant) => (
        <Avatar
          key={participant.fullName}
          src={participant.pictureUrl}
          hashValue={participant._id}
          alt={participant.fullName}
          size={25}
          className="font-medium"
        />
      ))}
    </span>
  );
}

function Activity({ getValue, row }: CellContext<ForumPost, string>) {
  const activity = getValue() ?? row.original.createdAt;

  return (
    <div className={classNames("whitespace-nowrap", "p-5 text-sm font-normal", "text-slate-600 dark:text-slate-400")}>
      <span>
        <RelativeTime timeStamp={parseISO(activity)} />
      </span>
    </div>
  );
}

function TopicItem({ getValue, row }: CellContext<ForumPost, string>) {
  return (
    <span
      className={classNames(
        "flex select-none items-center space-x-1",
        "whitespace-nowrap",
        "bg-slate-50 text-slate-600",
        "p-5 text-sm font-semibold text-slate-600",
        "group-hover:bg-violet-50 group-hover:text-violet-700",
      )}
    >
      {row.original.__typename === "ForumPost" && row.original.isLocked && (
        <LockIcon className="h-4 w-4 fill-blue-50" />
      )}

      {row.original.__typename === "ForumPost" && row.original.isPinned && <PinIcon className="h-4 w-4 fill-blue-50" />}

      <Link href={`/forum/${row.original.shortId}`}>{getValue()}</Link>
    </span>
  );
}
